/* eslint-disable @typescript-eslint/no-explicit-any */
import Peer from '@bowbee/peer-lite';

// Track the lifecycle of tracks across negotiations
const trackLifecycles = new Map<
  string,
  {
    originalId: string;
    replacements: Array<{
      timestamp: number;
      oldId: string;
      newId: string;
      state: string;
    }>;
  }
>();

// Helper function to safely add event listener without overriding existing ones
const addEventProxy = (target: any, eventName: string, handler: (...args: any[]) => void) => {
  const existingHandler = target[eventName];
  target[eventName] = function (...args: any[]) {
    // Call original handler if it exists
    if (existingHandler && typeof existingHandler === 'function') {
      existingHandler.apply(this, args);
    }
    // Call our debug handler
    handler.apply(this, args);
  };
};

// Helper function to monitor track states and errors
export const monitorTrack = (track: MediaStreamTrack, trackId: string, peerId: string) => {
  // Initialize track lifecycle tracking if new
  if (!trackLifecycles.has(trackId)) {
    trackLifecycles.set(trackId, {
      originalId: trackId,
      replacements: [],
    });
  }

  const logTrackState = (reason?: string) => {
    console.debug(`[DEBUG_Peer ${peerId}] Track ${trackId} state${reason ? ` (${reason})` : ''}:`, {
      readyState: track.readyState,
      enabled: track.enabled,
      muted: track.muted,
      settings: track.getSettings(),
      constraints: track.getConstraints(),
      lifecycle: trackLifecycles.get(trackId),
    });
  };

  // Log initial state
  logTrackState('initial');

  // Add proxied event handlers
  addEventProxy(track, 'onended', () => {
    console.error(`[DEBUG_Peer ${peerId}] Track ${trackId} ended unexpectedly`);
    logTrackState('ended event');
  });

  addEventProxy(track, 'onmute', () => {
    console.warn(`[DEBUG_Peer ${peerId}] Track ${trackId} muted`);
    logTrackState('mute event');
  });

  addEventProxy(track, 'onunmute', () => {
    console.info(`[DEBUG_Peer ${peerId}] Track ${trackId} unmuted`);
    logTrackState('unmute event');
  });

  // Monitor track readyState changes
  // let lastState = track.readyState;
  // const intervalId = setInterval(() => {
  //   const currentState = track.readyState;
  //   if (currentState !== lastState) {
  //     console.log(`[DEBUG_Peer ${peerId}] Track ${trackId} state changed from ${lastState} to ${currentState}`);
  //     logTrackState('state change');
  //     lastState = currentState;
  //   }
  // }, 1000);

  // return () => clearInterval(intervalId);
};

// Enhanced error monitoring for peer connection
export const monitorPeerConnection = (peer: Peer, peerId: string) => {
  const pc = peer.get();
  // let isNegotiating = false;
  let negotiationCount = 0;

  // Add proxied event handlers for peer connection
  addEventProxy(pc, 'onnegotiationneeded', () => {
    negotiationCount++;
    // isNegotiating = true;
    console.log(`[DEBUG_Peer ${peerId}] Negotiation #${negotiationCount} needed event triggered`);

    // Log all current tracks and their states
    pc.getSenders().forEach((sender) => {
      if (sender.track) {
        console.debug(`[DEBUG_Peer ${peerId}] Pre-negotiation track state:`, {
          trackId: sender.track.id,
          kind: sender.track.kind,
          readyState: sender.track.readyState,
          enabled: sender.track.enabled,
          lifecycle: trackLifecycles.get(sender.track.id),
        });
      }
    });
  });

  addEventProxy(pc, 'onsignalingstatechange', () => {
    console.log(`[DEBUG_Peer ${peerId}] Signaling state changed to: ${pc.signalingState}`);

    if (pc.signalingState === 'stable') {
      // isNegotiating = false;
      // Log post-negotiation track states
      pc.getSenders().forEach((sender) => {
        if (sender.track) {
          console.debug(`[DEBUG_Peer ${peerId}] Post-negotiation track state:`, {
            trackId: sender.track.id,
            kind: sender.track.kind,
            readyState: sender.track.readyState,
            enabled: sender.track.enabled,
            lifecycle: trackLifecycles.get(sender.track.id),
          });
        }
      });
    }
  });

  // ... rest of your existing event proxies ...
};

// Enhanced track handling functions
export const addTrackWithMonitoring = (rawPeer: Peer, track: MediaStreamTrack, peerId: string) => {
  try {
    const pc = rawPeer.get();
    console.log(`[DEBUG_Peer ${peerId}] Adding track:`, {
      trackId: track.id,
      kind: track.kind,
      readyState: track.readyState,
      connectionState: pc.connectionState,
      signalingState: pc.signalingState,
      lifecycle: trackLifecycles.get(track.id),
    });

    const cleanup = monitorTrack(track, track.id, peerId);
    rawPeer.addTrack(track);

    return cleanup;
  } catch (error) {
    console.error(`[DEBUG_Peer ${peerId}] Error adding track ${track.id}:`, error);
    throw error;
  }
};

export const replaceTrackWithMonitoring = (rawPeer: Peer, oldTrack: MediaStreamTrack, newTrack: MediaStreamTrack, peerId: string) => {
  try {
    const pc = rawPeer.get();
    console.log(`[DEBUG_Peer ${peerId}] Replacing track:`, {
      oldTrackId: oldTrack.id,
      newTrackId: newTrack.id,
      kind: newTrack.kind,
      connectionState: pc.connectionState,
      signalingState: pc.signalingState,
    });

    // Update track lifecycle tracking
    const lifecycle = trackLifecycles.get(oldTrack.id);
    if (lifecycle) {
      lifecycle.replacements.push({
        timestamp: Date.now(),
        oldId: oldTrack.id,
        newId: newTrack.id,
        state: oldTrack.readyState,
      });
      trackLifecycles.set(newTrack.id, lifecycle);
    }

    const cleanup = monitorTrack(newTrack, newTrack.id, peerId);
    rawPeer.replaceTrack(oldTrack, newTrack, false);

    return cleanup;
  } catch (error) {
    console.error(`[DEBUG_Peer ${peerId}] Error replacing track:`, error);
    throw error;
  }
};

export const verifyTransceiverSettings = (peer: Peer, trackId: string, expectedSettings: any) => {
  const pc = peer.get();
  const transceiver = pc.getTransceivers().find((t) => t.sender.track?.id === trackId);

  if (!transceiver) {
    console.warn(`No transceiver found for track ${trackId}`);
    return false;
  }

  const parameters = transceiver.sender.getParameters();
  console.log(`[Transceiver ${trackId}] Current settings:`, {
    direction: transceiver.direction,
    currentDirection: transceiver.currentDirection,
    encodings: (parameters as any).encodings,
    codec: parameters.codecs,
    expectedSettings,
  });

  return true;
};
